import Book from "@mui/icons-material/Book";
import FolderCopy from "@mui/icons-material/FolderCopy";
import Help from "@mui/icons-material/Help";
import QuestionAnswer from "@mui/icons-material/QuestionAnswer";
import Settings from "@mui/icons-material/Settings";
import Workspaces from "@mui/icons-material/Workspaces";
import Chip from "@mui/material/Chip";
import { createTheme as createMuiTheme, Theme } from "@mui/material/styles";
import * as React from "react";
import { DialogsProvider } from "../useDialogs";
import { IframeContext } from "../useIframe/useIframe";
import { Navigation } from "../useNavigation/navigation";
import { NavigationContext } from "../useNavigation/useNavigation";
import { NotificationsProvider } from "../useNotifications";
import useSession from "../useSession/useSession";
import { AppThemeProvider } from "./AppThemeProvider";

export interface AppProviderProps {
  /**
   * The content of the app provider.
   */
  children: React.ReactNode;
  /**
   * A [CSS variables theme](https://mui.com/material-ui/customization/css-theme-variables/overview/) is recommended.
   * @default createTheme()
   */
  theme?: Theme;
}

function createTheme(): Theme {
  return createMuiTheme({
    cssVariables: {
      colorSchemeSelector: "data-toolpad-color-scheme",
    },
    colorSchemes: { dark: true },
  });
}

function AppProvider(props: AppProviderProps) {
  const isIframe = window.self !== window.top;
  const { children, theme = createTheme() } = props;
  const [isExpanded, setIsExpanded] = React.useState<boolean>(true);
  const { data: session } = useSession();

  function getNavigation() {
    let navigation = [] as Navigation;
    if (Object.keys(session?.roles ?? {}).length > 0) {
      navigation.push({
        segment: "workspaces",
        title: "Mes espaces de travail",
        icon: <Workspaces style={{ color: "black" }} />,
      });
    }
    if ((session?.OrionUserGroups ?? []).length > 0) {
      navigation.push(
        {
          kind: "header",
          title: "Orion",
        },
        {
          kind: "divider",
        },

        {
          segment: "orion/assets",
          title: "Dossiers",
          icon: <FolderCopy style={{ color: "black" }} />,
        }
      );
      if ((session?.OrionContributorGroups ?? []).length > 0) {
        navigation.push(
          {
            segment: "orion/configs",
            title: "Configurations",
            icon: <Settings style={{ color: "black" }} />,
          },
          {
            segment: "orion/api",
            title: "Documentation",
            icon: <Help style={{ color: "black" }} />,
            action: <Chip label="Coming Soon" color="secondary" size="small" />,
          }
        );
      }
    }
    if ((session?.SiriusUserGroups?.length ?? 0) > 0) {
      navigation.push(
        {
          kind: "header",
          title: "Sirius",
        },
        {
          kind: "divider",
        },
        {
          segment: "sirius/bots",
          title: "Agents",
          icon: <QuestionAnswer style={{ color: "black" }} />,
        },
        {
          segment: "sirius/connectors",
          title: "Ma documentation",
          icon: <Book style={{ color: "black" }} />,
          action: <Chip label="Coming Soon" color="secondary" size="small" />,
        }
      );
    }
    return navigation;
  }

  return (
    <AppThemeProvider theme={theme}>
      <IframeContext.Provider value={{ isIframe }}>
        <NotificationsProvider>
          <DialogsProvider>
            <NavigationContext.Provider
              value={{ navigation: getNavigation(), isExpanded, setIsExpanded }}
            >
              {children}
            </NavigationContext.Provider>
          </DialogsProvider>
        </NotificationsProvider>
      </IframeContext.Provider>
    </AppThemeProvider>
  );
}

export { AppProvider };
